import { motion } from "framer-motion";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ShieldCheck, ShieldAlert, BadgeCheck, Download } from "lucide-react";
import { generateReportPdf } from "@/lib/reportPdf";

const fmt = (n) => (typeof n === "number" ? n.toLocaleString("it-IT") : n);

function Reveal({ children, delay = 0, testid }) {
  return (
    <motion.div
      data-testid={testid}
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

function SectionTitle({ eyebrow, title }) {
  return (
    <div className="mb-4">
      <p className="font-jetmono text-[9px] tracking-[0.35em] text-[#ff007f] mb-1.5">{eyebrow}</p>
      <h2 className="font-orbitron font-bold text-base sm:text-lg tracking-wider">{title}</h2>
    </div>
  );
}

function SpecRow({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-4 py-2.5 border-b border-white/5 last:border-0">
      <span className="text-[13px] text-[#8a8a9e]">{label}</span>
      <span className="font-orbitron text-[12px] font-bold text-white tracking-wider text-right">{value}</span>
    </div>
  );
}

function ChartTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0c0c10] border border-[#00f0ff]/40 rounded-lg px-3 py-2 font-jetmono text-[11px]">
      <span className="text-[#8a8a9e]">{payload[0].payload.year}</span>
      <span className="text-[#00f0ff] font-bold ml-2">{fmt(payload[0].payload.km)} km</span>
    </div>
  );
}

export default function ReportView({ report }) {
  const ins = report.insurance || {};
  const insuranceActive = ins.has_insurance && ins.compliant && !ins.suspended;
  const chartData = (report.mileage_history || []).map((m) => ({ ...m }));

  return (
    <div className="max-w-[600px] mx-auto px-5 pt-28 pb-20 space-y-6" data-testid="unlocked-report-container">
      <Reveal testid="report-header-card">
        <div className="vt-card relative overflow-hidden">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="font-jetmono text-[9px] tracking-[0.35em] text-[#00f0ff] mb-2">REPORT TECNICO CERTIFICATO</p>
              <h1 className="font-orbitron font-black text-2xl sm:text-3xl tracking-[0.15em]" data-testid="report-plate">
                {report.targa}
              </h1>
              <p className="font-orbitron text-sm text-white/80 mt-2 tracking-wider" data-testid="report-vehicle-name">
                {report.brand} {report.model}
              </p>
            </div>
            <BadgeCheck size={40} className="text-[#00ff88] shrink-0" data-testid="report-unlocked-badge" />
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-4 font-jetmono text-[9px] tracking-[0.2em]">
            <span className="px-2.5 py-1 rounded-md bg-[#00ff88]/10 border border-[#00ff88]/40 text-[#00ff88]">
              REPORT SBLOCCATO
            </span>
            {report.data_source === "demo" ? (
              <span className="px-2.5 py-1 rounded-md bg-amber-400/10 border border-amber-400/40 text-amber-300">
                FONTE: ARCHIVIO DEMO
              </span>
            ) : (
              <span className="px-2.5 py-1 rounded-md bg-[#00f0ff]/10 border border-[#00f0ff]/40 text-[#00f0ff]">
                FONTE: INFOTARGA
              </span>
            )}
          </div>
          <button
            onClick={() => generateReportPdf(report)}
            data-testid="download-pdf-button"
            className="mt-4 w-full bg-[#050507] hover:bg-[#141419] border border-[#00f0ff]/40 text-[#00f0ff] py-3 rounded-xl font-orbitron font-bold text-[11px] tracking-[0.2em] uppercase hover:shadow-[0_0_20px_rgba(0,240,255,0.3)] hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2 cursor-pointer"
          >
            <Download size={14} /> SCARICA IL REPORT IN PDF
          </button>
        </div>
      </Reveal>

      <Reveal testid="report-vehicle-section">
        <div className="vt-card">
          <SectionTitle eyebrow="SEZIONE 01" title="ANAGRAFICA VEICOLO" />
          <SpecRow label="Marca" value={report.brand} />
          <SpecRow label="Modello" value={report.model} />
          <SpecRow label="Immatricolazione" value={report.registration} />
          {report.registration_place && (
            <SpecRow label="Luogo Immatricolazione" value={report.registration_place} />
          )}
          {report.trim && <SpecRow label="Allestimento" value={report.trim} />}
          {report.vin && <SpecRow label="Numero Telaio (VIN)" value={report.vin} />}
          <SpecRow label="Tipologia" value={report.vehicle_type} />
          {report.body && report.body !== "N/D" && <SpecRow label="Carrozzeria" value={report.body} />}
        </div>
      </Reveal>

      <Reveal testid="report-engine-section">
        <div className="vt-card">
          <SectionTitle eyebrow="SEZIONE 02" title="DATI MOTORE & ALLESTIMENTO" />
          <SpecRow label="Cilindrata" value={`${fmt(report.engine_displacement_cc)} cc`} />
          <SpecRow label="Potenza" value={`${report.power_cv} CV`} />
          <SpecRow label="Alimentazione" value={report.fuel} />
          <SpecRow label="Classe Emissioni" value={report.euro_class} />
        </div>
      </Reveal>

      <Reveal testid="report-mileage-section">
        <div className="vt-card">
          <SectionTitle eyebrow="SEZIONE 03" title="STORICO CHILOMETRICO & REVISIONI" />
          <p className="font-jetmono text-[9px] tracking-[0.2em] text-[#8a8a9e]/70 -mt-2 mb-3">
            TREND CHILOMETRICO STIMATO DOVE NON CERTIFICATO
          </p>
          <div data-testid="mileage-history-chart" className="mb-4">
            <ResponsiveContainer width="100%" height={210}>
              <AreaChart data={chartData} margin={{ top: 8, right: 6, left: 6, bottom: 0 }}>
                <defs>
                  <linearGradient id="kmFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#00f0ff" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#00f0ff" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                <XAxis
                  dataKey="year"
                  tick={{ fill: "#8a8a9e", fontSize: 10, fontFamily: "JetBrains Mono" }}
                  axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
                  tickLine={false}
                />
                <YAxis hide domain={[0, "auto"]} />
                <Tooltip content={<ChartTooltip />} cursor={{ stroke: "#ff007f", strokeDasharray: "4 4" }} />
                <Area
                  type="monotone"
                  dataKey="km"
                  stroke="#00f0ff"
                  strokeWidth={2}
                  dot={{ r: 2.5, fill: "#ff007f", strokeWidth: 0 }}
                  activeDot={{ r: 5, fill: "#ff007f" }}
                  fill="url(#kmFill)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-2">
            {(report.revisions || []).length === 0 && (
              <p className="text-xs text-[#8a8a9e] font-jetmono">NESSUNA REVISIONE REGISTRATA</p>
            )}
            {(report.revisions || []).map((r) => (
              <div
                key={r.year}
                className="flex items-center justify-between gap-3 rounded-lg border border-white/5 bg-black/40 px-3 py-2.5"
                data-testid="revision-row"
              >
                <div className="flex items-center gap-3">
                  <span className="font-orbitron text-[12px] font-bold text-white">{r.year}</span>
                  <span className="font-jetmono text-[10px] text-[#8a8a9e]">{fmt(r.km)} km</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-jetmono text-[9px] text-[#8a8a9e] hidden sm:inline">{r.station}</span>
                  <span className="font-jetmono text-[9px] tracking-widest px-2 py-0.5 rounded bg-[#00ff88]/10 border border-[#00ff88]/40 text-[#00ff88]">
                    {r.esito}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Reveal>

      <Reveal testid="report-insurance-section">
        <div className="vt-card">
          <SectionTitle eyebrow="SEZIONE 04" title="STATO RCA & ASSICURAZIONE" />
          <div className="flex items-center gap-3 mb-4" data-testid="insurance-status-badge">
            {insuranceActive ? (
              <>
                <ShieldCheck size={22} className="text-[#00ff88]" />
                <span className="font-orbitron text-sm font-bold tracking-widest text-[#00ff88]">RCA ATTIVA</span>
              </>
            ) : (
              <>
                <ShieldAlert size={22} className="text-[#ff4d6d]" />
                <span className="font-orbitron text-sm font-bold tracking-widest text-[#ff4d6d]">RCA NON ATTIVA</span>
              </>
            )}
          </div>
          <SpecRow label="Compagnia" value={ins.insurer || "-"} />
          <SpecRow label="N. Polizza" value={ins.policy_number || "-"} />
          <SpecRow label="Scadenza Polizza" value={ins.policy_expiry || "-"} />
          <SpecRow label="Copertura fino al" value={ins.coverage_expiry || "-"} />
        </div>
      </Reveal>

      <p className="text-center font-jetmono text-[9px] tracking-[0.25em] text-[#8a8a9e]/70 pt-2">
        DOCUMENTO GENERATO IL{" "}
        {report.unlocked_at ? new Date(report.unlocked_at).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—"}{" "}
        · VERITARGA
      </p>
    </div>
  );
}
